// WHAT: Custom hook for managing the expansion state of TimeTracQ workspaces
// WHY:  Centralizes logic for expanding/collapsing individual workspaces and all workspaces, improving UX
// DOMAIN: Settings → TimeTracQ
// DEPENDENCIES: React hooks, WorkspaceConfig type
// RISKS:
//   - NOTE: `toggleAllWorkspaces` only affects workspaces with more than 4 aspects, which might not be intuitive.

import type { WorkspaceConfig } from "../types";

import { useState, useCallback } from "react";

/**
 * WHAT:
 *   Custom hook to manage the expanded/collapsed state of workspaces in the TimeTracQ configuration UI.
 *
 * WHY:
 *   Provides a centralized and reusable mechanism for controlling which workspaces are expanded,
 *   allowing users to focus on relevant sections and improving the overall user experience.
 *
 * PARAMETERS:
 *   - config: The current `WorkspaceConfig` state, used to determine which workspaces are expandable.
 *
 * RETURNS:
 *   An object containing the current set of expanded workspaces and functions to toggle
 *   individual or all workspaces, and to programmatically expand a specific workspace.
 *
 * @returns {object} Object with `expandedWorkspaces`, `expandWorkspace`, `toggleAllWorkspaces`, `toggleWorkspaceExpansion`
 *
 * RISKS:
 *   - NOTE: The `toggleAllWorkspaces` function only considers workspaces with more than 4 aspects
 *           for expansion/collapse, which might not align with all user expectations.
 *
 * @param config
 *
 */
export function useWorkspaceExpansion(config: null | WorkspaceConfig) {
  const [expandedWorkspaces, setExpandedWorkspaces] = useState<Set<string>>(new Set());

  /**
   * WHAT:
   *   Toggles the expanded state of a specific workspace.
   *
   * WHY:
   *   Allows users to individually expand or collapse workspaces to manage visibility of aspects.
   *
   * PARAMETERS:
   *   - workspaceName: string - The name of the workspace to toggle.
   *
   * SIDE EFFECTS:
   *   - Adds or removes the workspace name from the `expandedWorkspaces` Set, triggering a re-render.
   */
  const toggleWorkspaceExpansion = useCallback((workspaceName: string) => {
    setExpandedWorkspaces((previous) => {
      const newSet = new Set(previous);
      if (newSet.has(workspaceName)) {
        newSet.delete(workspaceName);
      } else {
        newSet.add(workspaceName);
      }
      return newSet;
    });
  }, []);

  /**
   * WHAT:
   *   Toggles the expanded state of all workspaces that have more than 4 aspects.
   *
   * WHY:
   *   Provides a convenient way for users to quickly expand or collapse multiple relevant workspaces.
   *
   * SIDE EFFECTS:
   *   - Updates the `expandedWorkspaces` Set to either include all expandable workspaces or clear it.
   *
   * RISKS:
   *   - NOTE: Only workspaces with more than 4 aspects are considered for this global toggle,
   *           which might not be the desired behavior in all scenarios.
   */
  const toggleAllWorkspaces = useCallback(() => {
    if (!config?.workspaces) {
      return;
    }

    const workspacesWithMoreThan4Aspects = Object.keys(config.workspaces).filter(
      (workspaceName) => Object.keys(config.workspaces[workspaceName] ?? {}).length > 4,
    );

    if (expandedWorkspaces.size === workspacesWithMoreThan4Aspects.length) {
      setExpandedWorkspaces(new Set());
    } else {
      setExpandedWorkspaces(new Set(workspacesWithMoreThan4Aspects));
    }
  }, [config, expandedWorkspaces.size]);

  /**
   * WHAT:
   *   Programmatically expands a specific workspace.
   *
   * WHY:
   *   Useful for scenarios like auto-expanding a workspace after a new aspect is added to it.
   *
   * PARAMETERS:
   *   - workspaceName: string - The name of the workspace to expand.
   *
   * SIDE EFFECTS:
   *   - Adds the workspace name to the `expandedWorkspaces` Set if it's not already present.
   */
  const expandWorkspace = useCallback((workspaceName: string) => {
    setExpandedWorkspaces((previous) => {
      const newSet = new Set(previous);
      newSet.add(workspaceName);
      return newSet;
    });
  }, []);

  return {
    expandedWorkspaces,
    expandWorkspace,
    toggleAllWorkspaces,
    toggleWorkspaceExpansion,
  };
}
